import React, { useEffect, useState, useRef, useCallback } from "react";
import { Zap, ChevronRight, ChevronDown, ChevronUp, X, AlertCircle, CheckCircle } from "lucide-react";
import { progressService } from "../../../../../services/progressService.js";
import { LiaSignalSolid } from "react-icons/lia";

const hasPositiveEnergy = (value) => {
  const num = typeof value === "number" ? value : Number(value);
  return Number.isFinite(num) && num > 0;
};

const buildItemsFromTasks = (tasks = []) => {
  const items = [];
  tasks.forEach((t) => {
    const hasSubtasks = Array.isArray(t.subtasks) && t.subtasks.length > 0;
    if (
      !hasSubtasks &&
      (t.category === "neutral" || t.category === "overhead") &&
      t.delegationQuadrant === "focus_yourself" &&
      hasPositiveEnergy(t.energy)
    ) {
      items.push({
        id: `t-${String(t.id)}`,
        type: "task",
        taskId: t.id,
        name: t.name,
        energy: t.energy ?? null,
        category: t.category ?? null,
        delegationQuadrant: t.delegationQuadrant ?? null,
        delegateTo: t.delegateTo ?? t.assignedTo ?? "",
        delegationConstraint: t.delegationConstraint ?? t.constraint ?? "",
      });
    }
    (t.subtasks || []).forEach((s) => {
      if (
        (s.category === "neutral" || s.category === "overhead") &&
        s.delegationQuadrant === "focus_yourself" &&
        hasPositiveEnergy(s.energy)
      ) {
        items.push({
          id: `s-${String(s.id)}`,
          type: "subtask",
          subtaskId: s.id,
          taskId: t.id,
          name: s.name,
          energy: s.energy ?? null,
          category: s.category ?? null,
          delegationQuadrant: s.delegationQuadrant ?? null,
          delegateTo: s.delegateTo ?? s.assignedTo ?? "",
          delegationConstraint: s.delegationConstraint ?? s.constraint ?? "",
        });
      }
    });
  });
  return items;
};

const buildTasksFromItems = (originalTasks = [], items = []) => {
  const constraintLookup = {};
  items.forEach((it) => {
    constraintLookup[it.id] = it.delegationConstraint ?? "";
  });

  return originalTasks.map((t) => {
    const taskItemId = `t-${String(t.id)}`;
    const taskConstraint =
      constraintLookup[taskItemId] ??
      t.delegationConstraint ??
      t.constraint ??
      "";

    const updatedSubs = (t.subtasks || []).map((s) => {
      const subItemId = `s-${String(s.id)}`;
      const subConstraint =
        constraintLookup[subItemId] ??
        s.delegationConstraint ??
        s.constraint ??
        "";
      return { ...s, delegationConstraint: subConstraint };
    });

    return { ...t, delegationConstraint: taskConstraint, subtasks: updatedSubs };
  });
};

const CONSTRAINT_OPTIONS = [
  { value: "hire", label: "I need to hire to delegate this task" },
  { value: "resources", label: "I have been denied resources" },
  { value: "train", label: "I need to train to delegate this task" },
  { value: "trust", label: "I cannot delegate since I do not trust them" },
];

/* ---------- Toast Notification Component ---------- */
const Toast = ({ message, type = "info", onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 5000);

    return () => clearTimeout(timer);
  }, [onClose]);

  const getIcon = () => {
    switch (type) {
      case "success":
        return <CheckCircle className="w-5 h-5 text-[#2E8B87] flex-shrink-0" />;
      case "error":
        return <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />;
      case "warning":
        return <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0" />;
      default:
        return <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />;
    }
  };

  const getBorderColor = () => {
    switch (type) {
      case "success":
        return "border-[#2E8B87]";
      case "error":
        return "border-red-600";
      case "warning":
        return "border-orange-600";
      default:
        return "border-blue-600";
    }
  };

  return (
    <div className="fixed top-6 right-6 z-50 animate-slideIn">
      <div className={`bg-white rounded-lg shadow-lg border ${getBorderColor()} px-6 py-4 flex items-center gap-3 min-w-[320px] max-w-[500px]`}>
        {getIcon()}
        <p className="text-sm font-gotham font-medium text-gray-800 flex-1">
          {message}
        </p>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close notification"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

const DelegationBlocker = ({ userId, subsectionId, onPreviousStep, onNextStep }) => {
  const service = progressService;

  const [tasks, setTasks] = useState([]);
  const [blockerTasks, setBlockerTasks] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [openDropdowns, setOpenDropdowns] = useState({});

  const loadedRef = useRef({ userId: null, subsectionId: null, done: false });

  // Toast state
  const [toastConfig, setToastConfig] = useState({ show: false, message: "", type: "info" });

  const showToast = (message, type = "info") => {
    setToastConfig({ show: true, message, type });
  };

  const hideToast = () => {
    setToastConfig({ show: false, message: "", type: "info" });
  };

  // Track previous constraint state to show toast only once
  const previousAllSelectedRef = useRef(false);

  useEffect(() => {
    async function fetchData() {
      if (!userId || !subsectionId) return;

      // Prevent duplicate loads
      if (
        loadedRef.current.done &&
        loadedRef.current.userId === userId &&
        loadedRef.current.subsectionId === subsectionId
      ) {
        return;
      }

      try {
        setLoading(true);

        const saved = await service.getUserProgress(userId, subsectionId);
        const savedTasks =
          saved?.data?.tasks ??
          saved?.tasks ??
          saved?.data?.progress?.tasks;

        let sourceTasks = savedTasks;
        if (!Array.isArray(sourceTasks) || sourceTasks.length === 0) {
          const userData = await service.getUserDataForSection(
            userId,
            "course_career_progression",
            "delegation",
            "delegation_widget"
          );
          sourceTasks = userData?.tasks || [];
        }

        const latestTasks = (sourceTasks || []).map((t, idx) => ({
          id: t.id ?? idx + 1,
          name: t.name ?? `Task ${idx + 1}`,
          energy: typeof t.energy === "number" ? t.energy : t.energy ? Number(t.energy) : null,
          category: t.category ?? null,
          delegationQuadrant: t.delegationQuadrant ?? null,
          delegateTo: t.delegateTo ?? t.assignedTo ?? "",
          delegationConstraint: t.delegationConstraint ?? t.constraint ?? "",
          subtasks: (t.subtasks || []).map((s, sidx) => ({
            id: s.id ?? `${Date.now()}-${sidx}`,
            name: s.name ?? `Subtask ${sidx + 1}`,
            energy: typeof s.energy === "number" ? s.energy : s.energy ? Number(s.energy) : null,
            category: s.category ?? null,
            delegationQuadrant: s.delegationQuadrant ?? null,
            delegateTo: s.delegateTo ?? s.assignedTo ?? "",
            delegationConstraint: s.delegationConstraint ?? s.constraint ?? "",
          })),
        }));

        const syncTasks = (savedList, latestList) => {
          if (!savedList || savedList.length === 0) {
            return latestList;
          }

          const savedMap = new Map(savedList.map(t => [t.id, t]));

          return latestList.map(latestTask => {
            const savedTask = savedMap.get(latestTask.id);

            if (!savedTask) {
              return latestTask;
            }

            return {
              ...latestTask,
              category: savedTask.category ?? latestTask.category,
              delegationQuadrant: savedTask.delegationQuadrant ?? latestTask.delegationQuadrant,
              delegateTo: savedTask.delegateTo ?? savedTask.assignedTo ?? latestTask.delegateTo ?? latestTask.assignedTo,
              delegationConstraint: savedTask.delegationConstraint ?? savedTask.constraint ?? latestTask.delegationConstraint ?? latestTask.constraint,
              subtasks: latestTask.subtasks.map(latestSub => {
                const savedSub = savedTask.subtasks?.find(s => s.id === latestSub.id);

                if (!savedSub) return latestSub;

                return {
                  ...latestSub,
                  category: savedSub.category ?? latestSub.category,
                  delegationQuadrant: savedSub.delegationQuadrant ?? latestSub.delegationQuadrant,
                  delegateTo: savedSub.delegateTo ?? savedSub.assignedTo ?? latestSub.delegateTo ?? latestSub.assignedTo,
                  delegationConstraint: savedSub.delegationConstraint ?? savedSub.constraint ?? latestSub.delegationConstraint ?? latestSub.constraint,
                };
              }),
            };
          });
        };

        const syncedTasks = syncTasks(savedTasks, latestTasks);
        const isDifferent = JSON.stringify(savedTasks || []) !== JSON.stringify(syncedTasks);
        if (isDifferent) {
          await service.saveProgress(
            userId,
            subsectionId,
            { tasks: syncedTasks },
            "in_progress"
          );
        }

        setTasks(syncedTasks);
        setBlockerTasks(buildItemsFromTasks(syncedTasks));
        loadedRef.current = { userId, subsectionId, done: true };
      } catch (err) {
        console.error("Failed to load blocker data:", err);
        setTasks([]);
        setBlockerTasks([]);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [userId, subsectionId, service]);

  // Check if all constraints are selected and show toast
  const areAllConstraintsSelected = useCallback(() => {
    return blockerTasks.every((item) => item.delegationConstraint && item.delegationConstraint.trim() !== "");
  }, [blockerTasks]);

  useEffect(() => {
    if (blockerTasks.length === 0) return;

    const allSelected = areAllConstraintsSelected();

    // Show toast when state changes from false to true
    if (allSelected && !previousAllSelectedRef.current) {
      showToast("✓ All constraints selected! Ready to proceed.", "success");
    }

    previousAllSelectedRef.current = allSelected;
  }, [blockerTasks, areAllConstraintsSelected]);

  const saveConstraints = async (updatedItems) => {
    try {
      console.log("💾 Saving constraints to backend...");
      const payloadTasks = buildTasksFromItems(tasks, updatedItems);
      
      await service.saveProgress(
        userId,
        subsectionId,
        { tasks: payloadTasks },
        "in_progress"
      );
      
      setTasks(payloadTasks);
      
      console.log("✅ Constraints saved successfully");
    } catch (err) {
      console.error("❌ Failed to save constraints:", err);
    }
  };

  const handleConstraintChange = (itemId, value) => {
    // Update state
    const updatedItems = blockerTasks.map((item) => 
      item.id === itemId ? { ...item, delegationConstraint: value } : item
    );
    
    setBlockerTasks(updatedItems);
    setOpenDropdowns((prev) => ({ ...prev, [itemId]: false }));
    
    // Save immediately
    saveConstraints(updatedItems);
  };

  const toggleDropdown = (itemId) => {
    setOpenDropdowns((prev) => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  // const handleSave = async () => {
  //   if (!service || typeof service.saveProgress !== "function") {
  //     console.warn("progressService.saveProgress not available.");
  //     return;
  //   }
  //   setIsSaving(true);
  //   try {
  //     const payloadTasks = buildTasksFromItems(tasks, blockerTasks);
  //     await service.saveProgress(userId, subsectionId, { tasks: payloadTasks }, "in_progress");
  //     setTasks(payloadTasks);
  //     showToast("Constraints saved successfully!", "success");
  //   } catch (err) {
  //     console.error("Save failed:", err);
  //     showToast("Failed to save constraints. Please try again.", "error");
  //   } finally {
  //     setIsSaving(false);
  //   }
  // };

  const handleFinish = async () => {
    if (!areAllConstraintsSelected()) {
      showToast("Please select constraints for all tasks before proceeding.", "warning");
      return;
    }

    if (!service || typeof service.saveProgress !== "function") {
      console.warn("progressService.saveProgress not available.");
      return;
    }
    setIsSaving(true);
    try {
      const payloadTasks = buildTasksFromItems(tasks, blockerTasks);
      await service.saveProgress(userId, subsectionId, { tasks: payloadTasks }, "completed");
      setTasks(payloadTasks);
      onNextStep?.();
    } catch (err) {
      console.error("Finish failed:", err);
      showToast("Failed to complete. Please try again.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const getCategoryBadge = (category) => {
    if (!category) return null;

    const badgeConfig = {
      neutral: { text: "N", bg: "bg-[#DFF3F1]", border: "border-[#54B0AF]" },
      overhead: { text: "O", bg: "bg-[#DFF3F1]", border: "border-[#54B0AF]" },
    };

    const config = badgeConfig[category];
    if (!config) return null;

    return (
      <span
        className={`${config.bg} ${config.border} border rounded-full px-3 py-1 text-[12px] font-gotham font-medium text-[#000000] flex items-center gap-1`}
      >
        {/* <span className="flex flex-col gap-[1px]">
          <span className="w-[2px] h-[2px] rounded-full bg-[#54B0AF]" />
          <span className="w-[2px] h-[2px] rounded-full bg-[#54B0AF]" />
          <span className="w-[2px] h-[2px] rounded-full bg-[#54B0AF]" />
        </span> */}
        <LiaSignalSolid className="w-3.5 h-3.5 text-[#1B7977]" />
        {config.text}
      </span>
    );
  };

  const getConstraintLabel = (value) => {
    const option = CONSTRAINT_OPTIONS.find((opt) => opt.value === value);
    return option ? option.label : "Choose Your Constraint";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[300px] text-gray-500">
        Loading blocker data...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-3">
      {/* Toast Notification */}
      {toastConfig.show && (
        <Toast
          message={toastConfig.message}
          type={toastConfig.type}
          onClose={hideToast}
        />
      )}

      <div className="max-w-[1200px] mx-auto">
        {blockerTasks.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-[18px] font-gotham text-[#666]">
              No neutral or overhead tasks found in "Focus deeply yourself" quadrant.
            </p>
            <p className="text-[14px] font-gotham text-[#999] mt-2">
              All your focus tasks are high leverage!
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start mb-3">
              <div>
                <h2 className="text-[16px] font-medium text-[#6E6E6E] font-gotham uppercase tracking-wide">
                  TASKS
                </h2>
              </div>
              <div>
                <h2 className="text-[16px] font-medium text-[#6E6E6E] font-gotham uppercase tracking-wide">
                  WHAT'S BLOCKING DELEGATION?
                </h2>
              </div>
            </div>

            <div className="space-y-3">
              {blockerTasks.map((item) => (
                <div
                  key={item.id}
                  className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch"
                >
                  <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-[9.82px] bg-white border border-[#E6E6E6] shadow-sm h-full">
                    <div className="flex items-center gap-3 flex-1">
                      <div className="text-[15.71px] font-gotham font-medium text-[#000000]">
                        {item.name}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <div className="bg-[#F4F4F4] border border-[#E2F2F1] rounded-full px-3 py-2 flex items-center gap-2">
                        <span className="text-[12px] text-[#000000] font-medium font-gotham">
                          {item.energy ?? "-"}%
                        </span>
                        <Zap className="w-4 h-4 text-[#54B0AF]" stroke="none" fill="#54B0AF" />
                      </div>
                      {getCategoryBadge(item.category)}
                    </div>
                  </div>

                  <div className="h-full relative">
                    <button
                      onClick={() => toggleDropdown(item.id)}
                      className={`w-full h-full px-4 py-3 rounded-[9.82px] border bg-white text-[15.71px] font-gotham text-left flex items-center justify-between transition-all ${
                        item.delegationConstraint
                          ? "border-[#54B0AF] text-[#000000]"
                          : "border-[#E6E6E6] text-[#CCCCCC]"
                      } hover:border-[#54B0AF] focus:outline-none focus:border-[#54B0AF] focus:ring-2 focus:ring-[#54B0AF]/20`}
                    >
                      <span className="flex-1">{getConstraintLabel(item.delegationConstraint)}</span>
                      {openDropdowns[item.id] ? (
                        <ChevronUp className="w-4 h-4 text-[#54B0AF]" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-[#54B0AF]" />
                      )}
                    </button>

                    {openDropdowns[item.id] && (
                      <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-[#E6E6E6] rounded-[9.82px] shadow-lg z-10 overflow-hidden">
                        {CONSTRAINT_OPTIONS.map((option) => (
                          <button
                            key={option.value}
                            onClick={() => handleConstraintChange(item.id, option.value)}
                            className="w-full px-4 py-3 text-left text-[15.71px] font-gotham text-[#000000] hover:bg-[#F0F9F8] transition-colors border-b border-[#F0F0F0] last:border-b-0"
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="w-full flex items-center justify-between gap-4 mt-12">
          <button
            onClick={onPreviousStep}
            className="flex items-center font-gotham font-medium gap-3 px-6 py-3 rounded-full bg-[#000000] text-white hover:bg-[#858585] transition-colors"
            aria-label="Previous Step"
          >
            <span className="bg-white rounded-full p-2">
              <ChevronRight className="w-4 h-4 rotate-180 text-black" />
            </span>
            Previous Step
          </button>

          <div className="flex items-center gap-4">
            {/* <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-6 py-3 rounded-full bg-[#2E8B87] text-white hover:bg-[#256e6b] transition-colors disabled:bg-gray-400"
            >
              {isSaving ? "Saving..." : "Save Progress"}
            </button> */}

            <button
              onClick={handleFinish}
              disabled={isSaving || !areAllConstraintsSelected()}
              className={`inline-flex items-center gap-4 px-6 py-3 rounded-full font-gotham font-medium text-lg transition-colors shadow ${
                areAllConstraintsSelected() && !isSaving
                  ? "bg-black text-white hover:bg-neutral-800"
                  : "bg-[#BDBDBD] text-white cursor-not-allowed"
              }`}
              aria-label="Next Step"
            >
              Next Step
              <span className="bg-white rounded-full p-2">
                <ChevronRight className="w-4 h-4 text-black" />
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Add animation styles */}
      <style jsx>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slideIn {
          animation: slideIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default DelegationBlocker;